import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link, NavLink } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import 'react-toastify/dist/ReactToastify.css';
import './Blogs.css';
import SecretCodeTopTimer from './SecretCode';
import SecretCodeBottomSection from './SecretBottomCode';
import { FaCalendarAlt, FaEye } from 'react-icons/fa';
import SubscribeBox from '../Component/Subscribe';
import { HomeContext } from '../Context/HomeContext';
import Loader from '../Component/Loader';
import AdBlockHorizontalMultiplex from '../Component/Ads/AdBlockHorizontalMultiplex';
import AdBlockHorizontal from '../Component/Ads/AdBlockHorizontal';
import AdBlockVertical from '../Component/Ads/AdBlockVertical';


const BlogDetails = () => {
  const { slug } = useParams();
  const { homeData } = useContext(HomeContext);
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({ full_name: '', email: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [secretCode, setSecretCode] = useState('');
  const [showBottomSection, setShowBottomSection] = useState(false);

  const categories = Array.isArray(homeData?.allCategories)
    ? homeData.allCategories
    : Object.values(homeData?.allCategories || {});


  const fetchBlogData = async () => {
    setBlogLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}website/blogs/${slug}`, {
        headers: {
          Accept: 'application/json',
          'Client-Domain': process.env.REACT_APP_CLIENT_DOMAIN,
        },
      });
      setBlog(res.data.blog || null);
      setRecentBlogs(res.data.recentBlogs || []);
      const approvedComments = (res.data.blog.comments || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setComments(approvedComments);
    } catch (error) {
      console.error('Error fetching blog data:', error);
    } finally {
      setBlogLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogData();
  }, [slug]);

  const handleInputChange = (e) => {
    setCommentForm({ ...commentForm, [e.target.name]: e.target.value });
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    const { full_name, email, description } = commentForm;
    if (!full_name || !email || !description) {
      toast.error('Please fill in all fields.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}website/blog/comment/store`,
        { blog_id: blog?.id, full_name, email, description },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Client-Domain': process.env.REACT_APP_CLIENT_DOMAIN,
          },
        }
      );

      const newComment = response.data.comment;
      newComment._localPreview = true;
      setComments((prev) => [newComment, ...prev]);
      setCommentForm({ full_name: '', email: '', description: '' });
      toast.success(response.data.message || 'Comment posted successfully!');
    } catch (err) {
      toast.error('Failed to post comment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const visibleComments = showAllComments ? comments : comments.slice(0, 3);

  return (
    <>
      <ToastContainer position="bottom-right" autoClose={3000} />

      {blog && (
        <Helmet>
          <title>{`${blog.title} - StreamPlay Network`}</title>
          <meta name="title" content={`${blog.title} - StreamPlay Network`} />
          <meta name="description" content={blog.short_description} />
          <meta name="keywords" content={blog.tags} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:title" content={`${blog.title} | StreamPlay Network`} />
          <meta property="og:description" content={blog.short_description} />
          <meta property="og:image" content={blog.image_500x500?.startsWith('http') ? blog.image_500x500 : `https://api.funfillia.com/${blog.image_500x500}`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${blog.title} | StreamPlay Network`} />
          <meta name="twitter:description" content={blog.short_description} />
          <meta name="twitter:image" content={blog.image_500x500?.startsWith('http') ? blog.image_500x500 : `https://api.funfillia.com/${blog.image_500x500}`} />
          <link rel="canonical" href={window.location.href} />
        </Helmet>
      )}

      <div className="blog-detail-container">
        <div className="main-blog">
          {blogLoading ? (
            <Loader text="Loading blog, please wait..." />
          ) : !blog ? (
            <div className="blog-not-found">Blog not found</div>
          ) : (
            <>
              <SecretCodeTopTimer
                onDecrypted={(code) => setSecretCode(code)}
                onTopTimerFinish={() => setShowBottomSection(true)}
              />

              <h1>{blog.title}</h1>
              <div className="meta-info">
                <span><FaCalendarAlt /> {new Date(blog.published_at).toLocaleDateString()}</span>
                <span><FaEye /> {blog.views_count} views</span>
                {/* <a href="#comments-section" style={{ textDecoration: 'none' }}>
                  <span className='commentCount'>💬 {blog.comment_count} comment{comments.length !== 1 ? 's' : ''}</span>
                </a> */}
              </div>

              <img
                src={blog.image_850x500?.startsWith('http') ? blog.image_850x500 : `https://api.funfillia.com/${blog.image_850x500}`}
                alt={blog.title}
                className="blog-banner"
              />

              <AdBlockHorizontalMultiplex />

              <div className="blog-content" dangerouslySetInnerHTML={{ __html: blog.content }} />

              <div className="nav-links">
                {blog.previousBlog && (
                  <Link to={`/blog/${blog.previousBlog.slug}`} className="prev">
                    ⬅ Previous
                  </Link>
                )}
                {blog.nextBlog && (
                  <Link to={`/blog/${blog.nextBlog.slug}`} className="next">
                    Next ➡
                  </Link>
                )}
              </div>

              {/* <div className="comments-section" id="comments-section">
                <h3>Leave a Comment</h3>
                <form onSubmit={handleCommentSubmit} className="comment-form">
                  <input type="text" name="full_name" placeholder="Your name" value={commentForm.full_name} onChange={handleInputChange} required />
                  <input type="email" name="email" placeholder="Your email" value={commentForm.email} onChange={handleInputChange} required />
                  <textarea name="description" placeholder="Write your comment..." value={commentForm.description} onChange={handleInputChange} rows={4} required />
                  <button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Posting...' : 'Post Comment'}
                  </button>
                </form>

                <p style={{ marginTop: '2rem' }}><strong>{blog.comment_count}</strong> Comment{comments.length !== 1 ? 's' : ''}</p>

                {visibleComments.length > 0 && (
                  <div className="comment-list" style={{ marginTop: '1rem' }}>
                    {visibleComments.map((c, i) => (
                      <div className="comment-item" key={i}>
                        <p><strong>{c.full_name}</strong></p>
                        <p>{c.description}</p>
                        <span className="comment-date">🕒 {new Date(c.created_at || Date.now()).toLocaleString()}</span>
                      </div>
                    ))}
                    {comments.length > 3 && (
                      <button className="toggle-comments-btn" onClick={() => setShowAllComments(!showAllComments)}>
                        {showAllComments ? 'Show Less' : 'Show More'}
                      </button>
                    )}
                  </div>
                )}
              </div> */}

              <AdBlockHorizontal />
              <SecretCodeBottomSection show={showBottomSection} secretCode={secretCode} />
            </>
          )}
        </div>

        <aside className="category-sidebar">
          <h3 className="sidebar-title1">Recent Blogs</h3>
          {recentBlogs.map(item => (
            <NavLink to={`/blog/${item.slug}`} key={item.id} className="sidebar-blog-item">
              <img src={`https://api.funfillia.com/${item.image_200x200}`} alt={item.title} />
              <div className="sidebar-blog-text">
                <h4>{item.title}</h4>
                <div className="sidebar-meta">
                  <span><FaCalendarAlt /> {new Date(item.published_at).toLocaleDateString()}</span>

                  <span><FaEye /> {item.views_count}</span>
                </div>
              </div>
            </NavLink>
          ))}
          <div className="sidebar-section">
            <div className="sidebar-title">Categories</div>
            <ul className="sidebar-category-list">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link to={`/category/${cat.slug}`} className="category-link">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <br></br>

          <AdBlockVertical />

        </aside>
      </div>

      <SubscribeBox />
    </>
  );
};

export default BlogDetails;
